import { db } from '../firebase';
import { collection, addDoc, updateDoc, doc, getDocs, query, where, orderBy, Timestamp } from 'firebase/firestore';
import stripeApiService from './stripeApiService';
import { STATUTS } from './accountingService';

/**
 * Service de paiement pour les clients
 * Gère l'attribution des produits, la création de factures et le traitement des paiements
 */
class ClientPaymentService {
  constructor() {
    this.clientProductsCollection = 'clientProducts';
    this.clientInvoicesCollection = 'clientInvoices';
  }

  /**
   * Attribue un produit Stripe à un client
   */
  async assignProductToClient(clientId, productId, options = {}) {
    try {
      console.log(`🔄 Attribution du produit ${productId} au client ${clientId}`);

      // Vérifier si le produit existe dans Stripe
      const products = await stripeApiService.getProductsWithPrices();
      const product = products.find(p => p.id === productId);

      if (!product) {
        throw new Error(`Produit Stripe non trouvé: ${productId}`);
      }

      // Vérifier si le produit est déjà attribué
      const existingAssignment = await this.getClientProductAssignment(clientId, productId);
      if (existingAssignment) {
        throw new Error('Ce produit est déjà attribué à ce client');
      }

      // Trouver le prix principal
      const mainPrice = product.prices.find(price => price.active) || product.prices[0];
      if (!mainPrice) {
        throw new Error('Aucun prix actif trouvé pour ce produit');
      }

      // Calculer la date de facturation suivante
      const now = new Date();
      let nextBillingDate = null;

      if (mainPrice.type === 'recurring') {
        if (mainPrice.recurring.interval === 'month') {
          nextBillingDate = new Date(now.getFullYear(), now.getMonth() + 1, now.getDate());
        } else if (mainPrice.recurring.interval === 'year') {
          nextBillingDate = new Date(now.getFullYear() + 1, now.getMonth(), now.getDate());
        }
      }

      // Créer l'attribution
      const assignmentData = {
        clientId,
        productId,
        stripePriceId: mainPrice.id,
        productName: product.name,
        productDescription: product.description,
        price: mainPrice.unit_amount,
        currency: mainPrice.currency,
        billingCycle: mainPrice.type === 'recurring' ? mainPrice.recurring.interval : 'one_time',
        status: 'active',
        assignedBy: options.assignedBy || 'system',
        assignedAt: Timestamp.now(),
        nextBillingDate: nextBillingDate ? Timestamp.fromDate(nextBillingDate) : null,
        metadata: product.metadata || {},
        customPrice: options.customPrice || null,
        notes: options.notes || ''
      };

      const docRef = await addDoc(collection(db, this.clientProductsCollection), assignmentData);

      // Créer la première facture si c'est un produit ponctuel
      // SAUF si l'option skipInvoiceCreation est activée (utilisée par stripeBillingService)
      if (mainPrice.type === 'one_time' && !options.skipInvoiceCreation) {
        await this.createInvoiceForProduct(clientId, productId, assignmentData);
      }

      console.log(`✅ Produit attribué avec succès: ${product.name} → Client ${clientId}`);
      return { id: docRef.id, ...assignmentData };

    } catch (error) {
      console.error('❌ Erreur lors de l\'attribution du produit:', error);
      throw error;
    }
  }

  /**
   * Récupère l'attribution d'un produit à un client
   */
  async getClientProductAssignment(clientId, productId) {
    try {
      const q = query(
        collection(db, this.clientProductsCollection),
        where('clientId', '==', clientId),
        where('productId', '==', productId),
        where('status', '==', 'active')
      );
      
      const snapshot = await getDocs(q);
      if (snapshot.empty) return null;
      
      const doc = snapshot.docs[0];
      return { id: doc.id, ...doc.data() };
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'attribution:', error);
      return null;
    }
  }

  /**
   * Récupère tous les produits attribués à un client
   */
  async getClientProducts(clientId) {
    try {
      const q = query(
        collection(db, this.clientProductsCollection),
        where('clientId', '==', clientId),
        where('status', '==', 'active'),
        orderBy('assignedAt', 'desc')
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Erreur lors de la récupération des produits client:', error);
      return [];
    }
  }

  /**
   * Crée une facture pour un produit attribué
   */
  async createInvoiceForProduct(clientId, productId, productData) {
    try {
      console.log(`📄 Création de facture pour produit ${productId}`);

      // Générer le numéro de facture
      const invoicesQuery = query(
        collection(db, this.clientInvoicesCollection),
        orderBy('invoiceNumber', 'desc')
      );
      const invoicesSnapshot = await getDocs(invoicesQuery);
      const lastInvoice = invoicesSnapshot.docs[0]?.data();
      const lastNumber = lastInvoice ? parseInt(lastInvoice.invoiceNumber.split('-')[1]) : 0;
      const invoiceNumber = `FAC-${(lastNumber + 1).toString().padStart(6, '0')}`;

      // Calculer les dates
      const now = new Date();
      const dueDate = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000); // 30 jours

      const invoiceData = {
        invoiceNumber,
        clientId,
        productId,
        productName: productData.productName,
        productDescription: productData.productDescription,
        amount: productData.customPrice || productData.price,
        currency: productData.currency,
        status: 'pending',
        createdAt: Timestamp.now(),
        dueDate: Timestamp.fromDate(dueDate),
        billingCycle: productData.billingCycle,
        stripePriceId: productData.stripePriceId,
        paymentIntentId: null,
        paidAt: null,
        paymentMethod: null,
        metadata: {
          type: 'product_payment',
          productCategory: productData.metadata?.category,
          productTarget: productData.metadata?.target
        }
      };

      const docRef = await addDoc(collection(db, this.clientInvoicesCollection), invoiceData);
      console.log(`✅ Facture créée: ${invoiceNumber}`);
      
      return { id: docRef.id, ...invoiceData };

    } catch (error) {
      console.error('❌ Erreur lors de la création de la facture:', error);
      throw error;
    }
  }

  /**
   * Récupère les factures d'un client et met à jour automatiquement les statuts en retard
   */
  async getClientInvoices(clientId, status = null) {
    try {
      let q;
      if (status) {
        q = query(
          collection(db, this.clientInvoicesCollection),
          where('clientId', '==', clientId),
          where('status', '==', status),
          orderBy('createdAt', 'desc')
        );
      } else {
        q = query(
          collection(db, this.clientInvoicesCollection),
          where('clientId', '==', clientId),
          orderBy('createdAt', 'desc')
        );
      }

      const snapshot = await getDocs(q);
      const invoices = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Vérifier et mettre à jour automatiquement les factures en retard
      const now = new Date();
      const invoicesToUpdate = [];

      for (const invoice of invoices) {
        // Si la facture n'est pas payée et a une date d'échéance dépassée
        if (
          invoice.status !== 'paid' &&
          invoice.status !== 'overdue' &&
          invoice.dueDate &&
          invoice.dueDate.toDate
        ) {
          const dueDate = invoice.dueDate.toDate();
          if (dueDate < now) {
            // Marquer comme en retard
            invoicesToUpdate.push({
              id: invoice.id,
              docRef: doc(db, this.clientInvoicesCollection, invoice.id)
            });
            invoice.status = 'overdue'; // Mettre à jour localement pour l'affichage immédiat
          }
        }
      }

      // Mettre à jour les statuts dans Firestore en arrière-plan (sans bloquer)
      if (invoicesToUpdate.length > 0) {
        Promise.all(
          invoicesToUpdate.map(({ docRef }) =>
            updateDoc(docRef, { status: 'overdue' })
          )
        ).catch(error => {
          console.error('Erreur lors de la mise à jour des statuts overdue:', error);
        });
      }

      return invoices;
    } catch (error) {
      console.error('Erreur lors de la récupération des factures client:', error);
      return [];
    }
  }

  /**
   * Crée un PaymentIntent Stripe pour une facture
   */
  async createPaymentIntent(invoiceId, clientEmail = null) {
    try {
      console.log(`💳 Création PaymentIntent pour facture ${invoiceId}`);

      // Récupérer la facture
      const invoiceDoc = await getDocs(query(collection(db, this.clientInvoicesCollection), where('__name__', '==', invoiceId)));
      if (invoiceDoc.empty) {
        throw new Error('Facture non trouvée');
      }

      const invoice = { id: invoiceDoc.docs[0].id, ...invoiceDoc.docs[0].data() };
      
      if (invoice.status === 'paid') {
        throw new Error('Cette facture est déjà payée');
      }

      // Utiliser le service Stripe existant pour créer le PaymentIntent
      const paymentIntent = await stripeApiService.makeStripeRequest('/payment_intents', 'POST', 
        new URLSearchParams({
          amount: invoice.amount.toString(),
          currency: invoice.currency || 'eur',
          metadata: JSON.stringify({
            invoiceId: invoice.id,
            invoiceNumber: invoice.invoiceNumber,
            clientId: invoice.clientId,
            productId: invoice.productId
          }),
          receipt_email: clientEmail || '',
          description: `Paiement pour ${invoice.productName} - ${invoice.invoiceNumber}`
        })
      );

      // Mettre à jour la facture avec l'ID du PaymentIntent
      await updateDoc(doc(db, this.clientInvoicesCollection, invoiceId), {
        paymentIntentId: paymentIntent.id,
        updatedAt: Timestamp.now()
      });

      console.log(`✅ PaymentIntent créé: ${paymentIntent.id}`);
      return paymentIntent;

    } catch (error) {
      console.error('❌ Erreur lors de la création du PaymentIntent:', error);
      throw error;
    }
  }

  /**
   * Traite un paiement réussi
   */
  async processSuccessfulPayment(paymentIntentId) {
    try {
      console.log(`✅ Traitement du paiement réussi: ${paymentIntentId}`);

      // Récupérer la facture par paymentIntentId
      const q = query(
        collection(db, this.clientInvoicesCollection),
        where('paymentIntentId', '==', paymentIntentId)
      );
      
      const snapshot = await getDocs(q);
      if (snapshot.empty) {
        throw new Error('Facture non trouvée pour ce PaymentIntent');
      }

      const invoiceDoc = snapshot.docs[0];
      const invoice = { id: invoiceDoc.id, ...invoiceDoc.data() };

      // Mettre à jour la facture
      await updateDoc(doc(db, this.clientInvoicesCollection, invoice.id), {
        status: 'paid',
        paidAt: Timestamp.now(),
        paymentMethod: 'stripe',
        updatedAt: Timestamp.now()
      });

      // Enregistrer dans le système comptable principal
      await this.recordPaymentInAccounting(invoice);

      console.log(`✅ Paiement traité pour facture ${invoice.invoiceNumber}`);
      return invoice;

    } catch (error) {
      console.error('❌ Erreur lors du traitement du paiement:', error);
      throw error;
    }
  }

  /**
   * Enregistre le paiement dans le système comptable principal
   */
  async recordPaymentInAccounting(invoice) {
    try {
      // Utiliser le service comptable existant pour enregistrer le paiement
      const accountingService = await import('./accountingService.js');
      
      const paymentData = {
        montant: invoice.amount / 100, // Convertir en euros
        mode: 'Stripe',
        reference: `STRIPE-${invoice.paymentIntentId}`,
        notes: `Paiement automatique pour ${invoice.productName} - ${invoice.invoiceNumber}`
      };

      // Créer une facture dans le système comptable principal si elle n'existe pas
      const existingFactures = await getDocs(
        query(collection(db, 'factures'), where('numero', '==', invoice.invoiceNumber))
      );

      if (existingFactures.empty) {
        // Créer une facture dans le système principal
        const factureData = {
          numero: invoice.invoiceNumber,
          clientId: invoice.clientId,
          clientNom: `Client ${invoice.clientId}`, // À améliorer avec les vraies données client
          montantHT: invoice.amount / 100,
          montantTTC: invoice.amount / 100,
          tva: 0,
          statut: STATUTS.FACTURE.PAYEE,
          dateEmission: invoice.createdAt,
          dateEcheance: invoice.dueDate,
          modePaiement: 'Stripe',
          notes: `Paiement produit: ${invoice.productName}`,
          historiquePaiements: [{
            id: crypto.randomUUID(),
            montant: invoice.amount / 100,
            date: Timestamp.now(),
            mode: 'Stripe',
            reference: `STRIPE-${invoice.paymentIntentId}`
          }]
        };

        await addDoc(collection(db, 'factures'), factureData);
      } else {
        // Mettre à jour la facture existante
        const factureDoc = existingFactures.docs[0];
        await accountingService.default.enregistrerPaiement(db, factureDoc.id, paymentData);
      }

      console.log(`📊 Paiement enregistré dans la comptabilité: ${invoice.invoiceNumber}`);

    } catch (error) {
      console.error('⚠️ Erreur lors de l\'enregistrement comptable:', error);
      // Ne pas faire échouer le processus de paiement principal
    }
  }

  /**
   * Annule l'attribution d'un produit à un client
   */
  async removeProductFromClient(clientId, productId) {
    try {
      const assignment = await this.getClientProductAssignment(clientId, productId);
      if (!assignment) {
        throw new Error('Produit non attribué à ce client');
      }

      await updateDoc(doc(db, this.clientProductsCollection, assignment.id), {
        status: 'cancelled',
        cancelledAt: Timestamp.now()
      });

      console.log(`❌ Produit retiré du client: ${productId}`);
      return true;

    } catch (error) {
      console.error('Erreur lors du retrait du produit:', error);
      throw error;
    }
  }

  /**
   * Récupère les statistiques de paiement d'un client
   */
  async getClientPaymentStats(clientId) {
    try {
      const [invoices, products] = await Promise.all([
        this.getClientInvoices(clientId),
        this.getClientProducts(clientId)
      ]);

      const paidInvoices = invoices.filter(inv => inv.status === 'paid');
      const pendingInvoices = invoices.filter(inv =>
        inv.status === 'pending' ||
        inv.status === 'sent' ||
        inv.status === 'overdue'
      );
      const totalPaid = paidInvoices.reduce((sum, inv) => sum + inv.amount, 0);
      const totalPending = pendingInvoices.reduce((sum, inv) => sum + inv.amount, 0);

      return {
        totalProducts: products.length,
        totalInvoices: invoices.length,
        paidInvoices: paidInvoices.length,
        pendingInvoices: pendingInvoices.length,
        totalPaid: totalPaid / 100, // Convertir en euros
        totalPending: totalPending / 100,
        lastPayment: paidInvoices.length > 0 ? paidInvoices[0].paidAt : null
      };
    } catch (error) {
      console.error('Erreur lors du calcul des statistiques:', error);
      return {
        totalProducts: 0,
        totalInvoices: 0,
        paidInvoices: 0,
        pendingInvoices: 0,
        totalPaid: 0,
        totalPending: 0,
        lastPayment: null
      };
    }
  }
}

// Instance singleton
const clientPaymentService = new ClientPaymentService();

export default clientPaymentService;
